'use client';
import type {ComponentProps, FC} from 'react';

import {assertUnreachable} from '@/shared/assertUnreachable';

import {ProductColorSelector} from '../../product-color/ProductColorSelector';
import {ProductSizeSelector} from '../../product-size/ProductSizeSelector';

import type {ClothingVariantFragment} from './query';
import {findVariantPropertiesForDefaultProperties} from './util';

type ClothingVariantSelectorProps = ComponentProps<'div'> & {
	variants: ClothingVariantFragment[];
	defaultVariantId?: string | null;
};

export const ClothingVariantSelector: FC<ClothingVariantSelectorProps> = ({
	variants,
	defaultVariantId,
	...rest
}) => {
	if (variants.length < 1) {
		return null;
	}

	let defaultVariant = variants.find((variant) => variant._key === defaultVariantId);

	if (!defaultVariant) {
		defaultVariant = variants[0];
	}

	let defaultSizeId: string | undefined;
	let defaultBaseColorId: string | undefined;
	let defaultPrintColorId: string | undefined;
	let defaultContrastColorId: string | undefined;

	for (const property of defaultVariant.properties ?? []) {
		switch (property._type) {
			case 'sizeProperty': {
				defaultSizeId = property.resolvedSize?._id;
				break;
			}
			case 'baseColorProperty': {
				defaultBaseColorId = property.resolvedColor?._id;
				break;
			}
			case 'printColorProperty': {
				defaultPrintColorId = property.resolvedColor?._id;
				break;
			}
			case 'contrastColorProperty': {
				defaultContrastColorId = property.resolvedColor?._id;
				break;
			}
			default:
				assertUnreachable(property);
		}
	}

	const {sizes, baseColors, printColors, contrastColors} =
		findVariantPropertiesForDefaultProperties(
			variants,
			defaultSizeId,
			defaultBaseColorId,
			defaultPrintColorId,
			defaultContrastColorId,
		);

	return (
		<div {...rest}>
			{sizes.length > 0 && (
				<ProductSizeSelector
					className="mb-8"
					legend="Størrelse"
					namePrefix="variantSizeId"
					sizes={sizes}
					defaultConfiguration={defaultSizeId ?? null}
				/>
			)}
			{baseColors.length == 1 && (
				<input type="hidden" name="variantBaseColorId" value={baseColors[0]._id} />
			)}
			{baseColors.length > 1 && (
				<ProductColorSelector
					className="mb-8"
					legend="Farge"
					namePrefix="variantBaseColorId"
					colors={baseColors}
					defaultConfiguration={defaultBaseColorId ?? null}
					disableNoneSelection
				/>
			)}
			{contrastColors.length == 1 && (
				<input type="hidden" name="variantPrintColorId" value={printColors[0]._id} />
			)}
			{printColors.length > 1 && (
				<ProductColorSelector
					className="mb-8"
					legend="Trykkfarge"
					namePrefix="variantPrintColorId"
					colors={printColors}
					defaultConfiguration={defaultPrintColorId ?? null}
					disableNoneSelection
				/>
			)}
			{contrastColors.length == 1 && (
				<input type="hidden" name="variantContrastColorId" value={contrastColors[0]._id} />
			)}
			{contrastColors.length > 1 && (
				<ProductColorSelector
					className="mb-8"
					legend="Kontrastfarge"
					namePrefix="variantContrastColorId"
					colors={contrastColors}
					defaultConfiguration={defaultContrastColorId ?? null}
					disableNoneSelection
				/>
			)}
		</div>
	);
};
